import { useState, useRef, useEffect } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import moment from 'moment';

import { Tooltip, Button } from '@gpstrackit/react-controls';
import { Icon } from "@mui/material";
import InputDate from 'Components/InputDate';
import { Typography } from "@mui/material";
import { NotificationManager } from "Components/Notifications";
//Components
import DataView from 'Components/DataView';
import Filters from 'Components/Filters';

import { getDateFormatted, getMinDateLimitBehaviour, getQuery } from 'Core/data/Helpers';

//Actions
import { accountsExportLogsRequest, clearRedux, accountsExportSearchUsersRequest } from 'Redux/actions';

//Style
import './index.scss';

const ExportLogs = (props) => {
    const { company, height } = props;
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const tableRef = useRef(null);

    const { loading, result, nextToken, users } = useSelector(state => state.accountExportLogsRedux);
    const lang = useSelector(state => state.commonRedux.language);

    const translateStatus = (status) => {
        if (!status) return "";
        const key = `accounts_export_status_${status.toLowerCase().replace(/\s+/g, '_')}`;
        return messages[key] || status;
    };

    const formatLocalizedDate = (date) => {
        if (!date) return "";
        const d = moment(date);
        if (!d.isValid()) return "";
        return d.locale(lang || 'en').format("MMM DD YYYY h:mm:ss A");
    };

    const [conditions, setConditions] = useState(null)
    const [columns, setColumns] = useState([
        {
            key: "name",
            title: messages['accounts_export_logs_name'],
            show: true,
            width: 250,
            code: "accounts_export_logs_name",
            render: (item) => {
                return <span>{item?.name}</span>
            }
        },
        {
            key: "createdOn",
            title: messages['accounts_export_logs_created_on'],
            show: true,
            width: 250,
            code: "accounts_export_logs_created_on",
            render: (item) => {
                return <span>{formatLocalizedDate(item?.createdOn)}</span>
            }
        },
        {
            key: "status",
            title: messages['accounts_export_status'],
            show: true,
            width: 200,
            code: "accounts_export_status",
            render: (item) => {
                return <span>{translateStatus(item?.status)}</span>
            }
        },
        {
            key: "statusUpdatedOn",
            title: messages['accounts_export_status_updated_on'],
            show: true,
            width: 250,
            code: "accounts_export_status_updated_on",
            render: (item) => {
                return <span>{formatLocalizedDate(item?.statusUpdatedOn)}</span>
            }
        },
        {
            key: "type",
            title: messages['accounts_export_type'],
            show: true,
            width: 100,
            code: "accounts_export_type",
            render: (item) => {
                return <span>{item?.type}</span>
            }
        },
        {
            key: "processedRecords",
            title: messages['accounts_export_processes_records'],
            show: true,
            width: 200,
            code: "accounts_export_processes_records",
            render: (item) => {
                return <span>{item?.processedRecords}</span>
            }
        },
        {
            key: "total",
            title: messages['accounts_export_total'],
            show: true,
            width: 100,
            code: "accounts_export_total",
            render: (item) => {
                return <span>{item?.total}</span>
            }
        },
        {
            key: "errorMessage",
            title: messages['accounts_export_logs_error_message'],
            show: true,
            width: 250,
            code: "accounts_export_logs_error_message",
            render: (item) => {
                return <span>{item?.errorMessage}</span>
            }
        },
        {
            key: "outputURL",
            title: messages['accounts_export_logs_output_url'],
            show: true,
            width: 70,
            code: "accounts_export_logs_output_url",
            render: (item) => {
                return (
                    <>
                        {
                            item?.outputURL ? (
                                <div>
                                    <a
                                        href={item?.outputURL}
                                        style={{ color: '#1D2734', textDecoration: 'underline', cursor: 'pointer' }}
                                        target="_blank"
                                    >
                                        <Tooltip title="Download File">
                                            <Icon>file_open</Icon>
                                        </Tooltip>
                                    </a>
                                </div>
                            ) : <></>
                        }
                    </>
                )
            }
        }
    ])

    const current_week = moment().startOf('week');
    const tempStartDate = new Date(current_week.toString());
    const [startDate, setStartDate] = useState(tempStartDate);
    const [endDate, setEndDate] = useState(new Date());
    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: tempStartDate,
        end: endDate
    })

    const [filters, setFilters] = useState({
        startDate: tempStartDate,
        endDate: endDate,
    })

    const [last, setLast] = useState({ id: '20', name: '20' });
    const [type, setType] = useState('last');

    const onChange = (value) => {
        const { start, end } = value;
        let endDate = end ? end.setHours(23, 59, 59) : null
        onChangeStartEndDate({
            start,
            end: endDate
        })
    };

    const onChangeStartEndDate = ({ start, end }) => {
        const newStart = start && new Date(start);
        const newEnd = end && new Date(end);

        let fltrs = filters;
        fltrs.startDate = newStart;
        fltrs.endDate = newEnd;
        // Set New Date Range
        if (start && end) {
            const newDateRange = {
                start,
                end,
            };
            setBeforeRangeDate(newDateRange)
        }
        setFilters({ ...fltrs });
    }

    const onCloseSelectDate = () => {
        const { start, end } = beforeRangeDate;
        if (start && end && !endDate) {
            let fltrs = filters;
            fltrs.startDate = start;
            fltrs.endDate = end;
            setFilters({ ...fltrs });
        }
    }

    const getData = (refresh, search, suggestedFilters, firstLoad, newLast, newType) => {
        let currentFilters = filters
        let currentType = type
        let currentLast = last

        if (suggestedFilters)
            currentFilters = suggestedFilters

        if (newType)
            currentType = newType

        if (newLast)
            currentLast = newLast

        let payload = {
            accountId: company?.id,
            projectId: "px-cloud",
            limit: currentType === 'last' ? currentLast.id : 50,
            nextToken: !refresh ? typeof search === undefined ? nextToken : null : null
        }
        if (currentType !== 'last') {
            payload.dateRange = {
                start: currentFilters?.startDate,
                end: currentFilters?.endDate
            }
        } else {
            payload.lastRecords = currentLast.id
        }

        if (refresh || typeof search === undefined) {
            dispatch(clearRedux("ACCOUNTS_EXPORT_LOGS", { nextToken: null }))
        }
        setConditions(payload)
        dispatch(accountsExportLogsRequest(payload))

        if (!firstLoad) tableRef?.current?.conditionsHandler(payload)
    }

    const applyFilters = () => {
        if (filters.startDate && filters.endDate) {
            getData(true);
        } else {
            NotificationManager.warning(
                messages['raw_data_tab_date_range'],
                messages['raw_data_tab_raw_data_modal_title'],
                5000,
            );
        }
    }

    useEffect(() => {
        dispatch(accountsExportSearchUsersRequest(company?.id))
        return () => {
            setConditions(null)
            dispatch(clearRedux("ACCOUNTS_EXPORT_LOGS"))
        }
    }, [])

    useEffect(() => {
        setColumns(currentColumns => currentColumns.map(col => ({
            ...col,
            title: col.code ? messages[col.code] : col.title,
            render: col.key === "createdOn" ? (item) => <span>{formatLocalizedDate(item?.createdOn)}</span>
                : col.key === "status" ? (item) => <span>{translateStatus(item?.status)}</span>
                : col.key === "statusUpdatedOn" ? (item) => <span>{formatLocalizedDate(item?.statusUpdatedOn)}</span>
                : col.render,
        })));
    }, [lang]);


    return (
        <div style={{ marginTop: 10 }}>
            <DataView
                name="pm-export-logs-tab"
                exportName="pmexportlogs"
                externalLoading={loading}
                externalData={result?.length ? result : []}
                ref={tableRef}
                columns={columns}
                height={height}
                noSplit
                noPDF
                isDynamo
                infiniteScroll
                disableItemSelection
                noFilters
                noSearch
                noTags
                noExcel
                nextToken={nextToken}
                onChangeColumns={(items) => setColumns(items)}
                extraFilters={() => {
                    return (
                        <div className="scheduled-report-filters" style={{ width: "100%", display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <div className="d-flex">
                                <div>
                                    <Filters

                                        onChangeFilters={() => applyFilters()}
                                        startDate={startDate}
                                        endDate={endDate}
                                        setStartDate={setStartDate}
                                        setEndDate={setEndDate}
                                        setBeforeRangeDate={setBeforeRangeDate}
                                        beforeRangeDate={beforeRangeDate}
                                        onChange={onChange}
                                        onChangeStartEndDate={onChangeStartEndDate}
                                        last={last}
                                        setLast={setLast}
                                        type={type}
                                        setType={setType}
                                    />
                                </div>

                            </div>
                        </div>
                    )
                }}
                onChangePage={(token) => getData()}
                query={getQuery("scheduled-report")}
                conditions={conditions}
                tableButtons={['orderColumn']}
                externalRefresh={() => getData(true)}
                onChangeConditions={(tags, newConditions, total, newColumns, rows, sort) => {

                    let fltrs = filters
                    let currentType = null
                    let currentLast = null
                    if (newConditions?.dateRange) {
                        fltrs.startDate = new Date(newConditions?.dateRange?.start)
                        fltrs.endDate = new Date(newConditions?.dateRange?.end)
                        setFilters({ ...fltrs })
                        setType('range')
                        currentType = 'range'
                    } else {
                        setType('last')
                        currentType = 'last'
                    }

                    if (rows) {
                        setLast({ id: `${rows}`, name: `${rows}` })
                        currentLast = { id: `${rows}`, name: `${rows}` }
                    }
                    getData(false, false, fltrs, true, currentLast, currentType);
                }}

            />
        </div>
    )
}

export default ExportLogs;